import { InputType } from '@/types';
import type { IInput } from '@/types';
import isBlobUrl from '@/utils/file/isBlobUrl';
import isDataUrl from '@/utils/file/isDataUrl';
import isInputEmpty from '@/utils/isInputEmpty';

/**
 * The Generate button gate: every input must be populated with usable data
 * — non-empty text or a CDN-hosted media URL that's finished uploading.
 * Local previews (`blob:` from device uploads, `data:` base64 from canvas)
 * are rejected so a temporary preview can never be submitted to the webhook.
 *
 * An `optional` input left entirely empty is skipped — the flow accepts it
 * missing, and gating on it would deadlock the single-required-input picker
 * flow (nothing ever fills the optional slot, so generation would never arm).
 * Once an optional input DOES carry a value it faces the same strict check as
 * a required one, so a half-uploaded optional photo still can't be submitted.
 */
const isFormValid = (inputs: IInput[]): boolean =>
  inputs.every((input) => {
    if (input.optional && isInputEmpty(input)) return true;
    if (input.type === InputType.TEXT) {
      return (input.value ?? '').trim().length > 0;
    }
    return (
      Boolean(input.src) &&
      !isBlobUrl(input.src) &&
      !isDataUrl(input.src) &&
      !input.isLoading
    );
  });

export default isFormValid;
